import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getYouTubeSearchHistory } from '../api/lfApiYouTube';
import { useError } from '../contexts/ErrorContext';

const SearchBox = ({
  value,
  onChange,
  onSubmit,
  onClear,
  isSearchVisible,
  setSearchVisible
}) => {
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const { setError } = useError();
  const searchRef = useRef(null);
  const latestRequestRef = useRef(0);

  const navigate = useNavigate();
  const location = useLocation();

  const [internalKeyword, setInternalKeyword] = useState('');
  const keyword = value !== undefined ? value : internalKeyword;

  const fetchHistory = useCallback(async (params = {}) => {
    const requestId = ++latestRequestRef.current;
    try {
      const { items } = await getYouTubeSearchHistory(params, setError);
      if (requestId === latestRequestRef.current) {
        setHistory(items || []);
        setShowHistory(true);
      }
    } catch (err) {
      console.error('Failed to fetch search history', err);
    }
  }, [setError]);

  const setKeyword = (val) => {
    if (onChange) {
      onChange(val);
    } else {
      setInternalKeyword(val);
    }

    if (val.trim()) {
      fetchHistory({ prefix: val, max_results: 10 });
    } else {
      // If cleared via typing, show full history again or hide? 
      // User's diff showed hiding, but usually showing recent is better.
      // Let's fetch general history when empty for better UX.
      fetchHistory();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowHistory(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const searchKeyword = searchParams.get('k');
    if (searchKeyword && setSearchVisible) {
      setSearchVisible(true);
      if (!onChange) {
        setInternalKeyword(searchKeyword);
      }
    }
  }, [location.search, setSearchVisible, onChange]);

  useEffect(() => {
    if (isSearchVisible) {
      const searchInput = document.getElementById('search-input');
      if (searchInput) {
        searchInput.focus();
      }
    }
  }, [isSearchVisible]);

  const handleHistoryClick = (query) => {
    setKeyword(query);
    setShowHistory(false);

    // Blur the search input to hide the dropdown until it regains focus
    const searchInput = document.getElementById('search-input');
    if (searchInput) {
      searchInput.blur();
    }

    if (onSubmit) {
      onSubmit(query);
    } else {
      _applyInternalSearch(query);
    }
  };

  const _applyInternalSearch = (kw) => {
    const searchParams = new URLSearchParams(window.location.search);
    if (kw) {
      searchParams.set('k', kw);
    } else {
      searchParams.delete('k');
    }
    navigate(`?${searchParams.toString()}`);
  }

  const toggleSearch = async () => {
    const newIsSearchVisible = !isSearchVisible;
    if (setSearchVisible) {
      setSearchVisible(newIsSearchVisible);
    }

    if (!newIsSearchVisible) {
      setKeyword('');
      if (onClear) {
        onClear();
      } else {
        _applyInternalSearch('');
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      setShowHistory(false);
      if (onSubmit) {
        onSubmit(keyword);
      } else {
        _applyInternalSearch(keyword);
      }
    }
  };

  const handleSearchFocus = () => {
    setShowHistory(true);
    fetchHistory(keyword.trim() ? { prefix: keyword, max_results: 10 } : {});
  };

  const handleClearClick = (e) => {
    e.stopPropagation();
    setKeyword('');
    if (onClear) {
      onClear();
    } else {
      _applyInternalSearch('');
    }
  };

  const isToggleMode = isSearchVisible !== undefined;
  const showInput = !isToggleMode || isSearchVisible;

  return (
    <div className={`relative inline-block text-center py-2 ${!isToggleMode ? 'w-full' : ''}`} ref={searchRef}>
      {isToggleMode && (
        <div
          onClick={toggleSearch}
          className="cursor-pointer text-xl"
          title="Toggle Search"
        >
          🔎
        </div>
      )}

      {showInput && (
        <div className={isToggleMode ? "absolute top-0 left-7 transform w-max rounded p-1 z-10" : "w-full relative"}>
          <div className="relative flex items-center">
            <input
              type="text"
              placeholder="What do you want to learn?"
              onKeyDown={handleKeyDown}
              onFocus={handleSearchFocus}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              id="search-input"
              autoComplete="off"
              className="w-full p-2 pr-10 border border-gray-300 rounded shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
            {keyword && (
              <button
                onClick={handleClearClick}
                className="absolute right-3 text-gray-400 hover:text-gray-600 focus:outline-none text-xl"
                title="Clear search"
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
          {showHistory && history.length > 0 && (
            <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-50 overflow-hidden text-left">
              {history.map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleHistoryClick(item.query)}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:bg-gray-100 outline-none transition-colors"
                  aria-label={item.query}
                >
                  <span className="mr-2 text-gray-400">🕒</span>
                  {item.query}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBox;
